const INFINITY_SYMBOL = '\u221E';

const clampIndex = (value, count) => {
    if (!Number.isFinite(value) || count <= 0) {
        return null;
    }
    return Math.max(0, Math.min(count - 1, value));
};

export const buildMatrixLabels = (count, isFlipped) => {
    const values = Array.from({ length: count }, (_, idx) => idx);
    if (isFlipped) values.reverse();
    return values.map(value => String(value));
};

export const buildSequentialLabels = (count, isFlipped, start = 1) => {
    const values = Array.from({ length: count }, (_, idx) => start + idx);
    if (isFlipped) values.reverse();
    return values.map(value => String(value));
};

export const buildEndzoneRowLabels = (count, isFlipped) => {
    if (count < 3) {
        return buildSequentialLabels(count, isFlipped);
    }

    const lastIndex = count - 1;
    const topGoalIndex = Math.min(1, lastIndex);
    const bottomGoalIndex = Math.max(lastIndex - 1, topGoalIndex);
    const centerIndex = Math.floor((topGoalIndex + bottomGoalIndex) / 2);

    // Build numeric labels (without infinities)
    const numericLabels = Array.from({ length: count }, (_, idx) => {
        if (idx === 0 || idx === lastIndex) {
            return null; // Placeholder for infinities
        }

        if (idx <= centerIndex) {
            return String(Math.max(0, idx - topGoalIndex));
        }
        return String(Math.max(0, bottomGoalIndex - idx));
    });

    // Flip only the numeric portion if needed
    if (isFlipped) numericLabels.reverse();

    // Always put +∞ at top (index 0) and -∞ at bottom (last index)
    // This ensures current player's goal direction is always +∞ at top
    numericLabels[0] = `+${INFINITY_SYMBOL}`;
    numericLabels[lastIndex] = `-${INFINITY_SYMBOL}`;

    return numericLabels;
};

export const formatBoardCoordinate = ({
    position,
    numRows,
    numCols,
    coordSystem = 'endzone',
    viewPerspective = 1,
}) => {
    if (!Array.isArray(position) || position.length < 2) {
        return { rowLabel: '—', colLabel: '—', formatted: '—' };
    }

    const rows = Math.max(0, numRows ?? 0);
    const cols = Math.max(0, numCols ?? 0);
    if (!rows || !cols) {
        return { rowLabel: '—', colLabel: '—', formatted: '—' };
    }

    const flipRow = coordSystem !== 'matrix'
        && (viewPerspective === 2 || viewPerspective === 'south');

    const rowIndex = clampIndex(position[0], rows);
    const colIndex = clampIndex(position[1], cols);

    if (rowIndex == null || colIndex == null) {
        return { rowLabel: '—', colLabel: '—', formatted: '—' };
    }

    const displayRow = flipRow ? (rows - 1 - rowIndex) : rowIndex;
    const displayCol = colIndex;

    const rowLabels = coordSystem === 'matrix'
        ? buildMatrixLabels(rows, false)
        : buildEndzoneRowLabels(rows, false);
    const colLabels = coordSystem === 'matrix'
        ? buildMatrixLabels(cols, false)
        : buildSequentialLabels(cols, false);

    let rowLabel = rowLabels[displayRow] ?? '—';
    const colLabel = colLabels[displayCol] ?? '—';

    if (
        coordSystem === 'endzone'
        && rowLabel
        && !rowLabel.includes(INFINITY_SYMBOL)
    ) {
        const numeric = Number(rowLabel);
        if (Number.isFinite(numeric) && numeric !== 0) {
            const topGoalIndex = Math.min(1, rows - 1);
            const bottomGoalIndex = Math.max(rows - 1 - 1, topGoalIndex);
            const centerIndex = Math.floor((topGoalIndex + bottomGoalIndex) / 2);
            if (displayRow === centerIndex) {
                rowLabel = `${Math.abs(numeric)}`;
            } else {
                const sign = displayRow <= centerIndex ? '+' : '-';
                rowLabel = `${sign}${Math.abs(numeric)}`;
            }
        } else if (numeric === 0) {
            rowLabel = '0';
        }
    }

    return {
        rowLabel,
        colLabel,
        formatted: `${rowLabel}, ${colLabel}`,
    };
};

export { INFINITY_SYMBOL };
